import React, { useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteLogbookModal from '../components/DeleteLogbook';
import AddLogbookTemplateModal from '../components/AddLogbookTemplateModal';
import { LogbookTemplate } from '../types/loogBookTypes';
import CustomIconButton from '../../../core-components/theme/components/Button/Iconbutton';
import { Neutrals } from '../../../core-components/theme/color-palette/colors';
import EditIcon from '../../../core-components/assets/icons/EditIcon';
import DeleteFilledIcon from '../../../core-components/assets/icons/DeleteIconFilled';
import { useQueryClient } from '@tanstack/react-query';
import { fetchLogbookOptions, fetchTemplateDetails } from '../api/logbookApi';

interface LogbookActionsProps {
    row: LogbookTemplate;
    queryKey: any;
    onEditHover?: (row: LogbookTemplate) => void;
    disabled?: boolean;
}

const LogbookActions: React.FC<LogbookActionsProps> = ({
    row,
    queryKey,
    onEditHover,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedAction, setSelectedAction] = useState<string | null>(null);

    const queryClient = useQueryClient();

    const prefetchOptions = () => {
        queryClient.prefetchQuery({
            queryKey: ['logbookOptions'],
            queryFn: fetchLogbookOptions,
            staleTime: 60000,
        });
    };

    const prefetchDetails = () => {
        queryClient.prefetchQuery({
            queryKey: ['logbookTemplate', row.logbookTemplateId],
            queryFn: () => fetchTemplateDetails(row.logbookTemplateId!),
            staleTime: 60000,
        });
    };

    const prefetchData = () => {
        prefetchOptions();
        prefetchDetails();
    };

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;
        event?.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const closeMenu = (event: React.MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        setMenuAnchorEl(null);
    };

    const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        setSelectedAction('delete');
        closeMenu(event);
    };

    const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        setSelectedAction('edit');
        closeMenu(event);
    };

    const handleCloseModal = (event?: React.MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        setSelectedAction(null);
    };

    return (
        <React.Fragment>
            <CustomIconButton onClick={openMenu} icon={MoreVertIcon} disabled={disabled} />

            <Menu
                anchorEl={menuAnchorEl}
                open={!!menuAnchorEl}
                onClose={closeMenu}
                slotProps={{
                    paper: {
                        style: {
                            boxShadow: '0px 2px 8px 0px #1A335626',
                        },
                    },
                }}
                MenuListProps={{
                    sx: {
                        border: `1px solid ${Neutrals[300]}`,
                        py: 0,
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                    },
                }}
            >
                <MenuItem
                    sx={{
                        height: '36px',
                        paddingLeft: '8px',
                        borderBottom: `1px solid ${Neutrals[300]}`,
                    }}
                    onClick={handleEdit}
                    onMouseEnter={prefetchData}
                >
                    <EditIcon />
                    <Typography ml={2} color={Neutrals[500]}>
                        {t('common.edit', 'Edit')}
                    </Typography>
                </MenuItem>

                <MenuItem onClick={handleDelete} sx={{ height: '36px', paddingLeft: '8px' }}>
                    <DeleteFilledIcon />
                    <Typography ml={2} color={Neutrals[500]}>
                        {t('common.delete', 'Delete')}
                    </Typography>
                </MenuItem>
            </Menu>

            {selectedAction === 'edit' && (
                <AddLogbookTemplateModal open={true} initialData={row} onClose={handleCloseModal} />
            )}

            {selectedAction === 'delete' && (
                <DeleteLogbookModal data={row} handleClose={handleCloseModal} queryKey={queryKey} />
            )}
        </React.Fragment>
    );
};

export default LogbookActions;
